import CheckIcon from "@heroicons/react/16/solid/CheckIcon";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import React, { ReactNode, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { Tooltip } from "@fronterahealth/frontera-ui-components";
import { Button } from "@fronterahealth/frontera-ui-components/ui/button";

import { ReviewItemFieldsFragment } from "@api/graphql/types-and-hooks";
import { CheckAction, CheckActionState } from "@components/QaReview/components/CommentActionResolve";
import { SqueezeLevel, TIGHTEST_SQUEEZE, nextSqueezeLevel } from "@components/QaReview/components/reviewBarLayout";

interface SuggestionNavigatorProps {
  items: ReviewItemFieldsFragment[];
  activeId: string | null;
  onActivate: (item: ReviewItemFieldsFragment) => void;
  /** The view picker, which names the view the items come from. */
  picker: ReactNode;
  /** Checkpoint's mark and Quality Review's state, leading the bar; the last squeeze shrinks the words to icons. */
  status?: (isIconOnly: boolean) => ReactNode;
  /** The most room the bar has; only past it does it squeeze (see SqueezeLevel). */
  maxWidth?: number;
  /** Whether anything in the review is open, so an empty list reads as a narrow view, not a finished review. */
  hasOpenItems: boolean;
  isDefaultView: boolean;
  /** The list isn't known yet (the cards haven't published their order), so show no count. */
  isPending?: boolean;
  onShowAllOpen: () => void;
}

interface NavButtonProps {
  icon: React.ElementType;
  onClick: () => void;
  disabled?: boolean;
  tooltip: string;
}

// aria-disabled, not disabled: a disabled button drops keyboard focus to the page.
const NavButton: React.FC<NavButtonProps> = ({ icon: Icon, onClick, disabled = false, tooltip }) => {
  const button = (
    <button
      type="button"
      aria-label={tooltip}
      aria-disabled={disabled}
      onClick={() => {
        if (!disabled) onClick();
      }}
      className={twMerge(
        "flex size-10 shrink-0 items-center justify-center rounded-md transition-colors",
        disabled ? "cursor-not-allowed text-limestone-300" : "cursor-pointer text-text-primary hover:bg-limestone-100",
      )}
    >
      <Icon className="h-4 w-4" strokeWidth={2} />
    </button>
  );

  return (
    <Tooltip text={tooltip} showInfoIcon={false} placement="top">
      {button}
    </Tooltip>
  );
};

const ACCEPT_LABEL = "Accept and resolve";

// Holds the room of the widest text the slot can show (data-widest, drawn invisibly under its text), so the bar
// keeps its width and the arrows their place as the text changes from card to card. The text goes in a child
// with RESERVED_TEXT.
const RESERVES_WIDEST =
  "inline-grid before:invisible before:col-start-1 before:row-start-1 before:content-[attr(data-widest)]";
const RESERVED_TEXT = "col-start-1 row-start-1";

// Resolve is the bar's main action, so it keeps its label at every squeeze.
const ResolveButton: React.FC<{ action: CheckActionState | null }> = ({ action }) => {
  const descriptionId = useId();
  const disabled = !action || action.disabled;
  const label = action?.kind === "accept" ? ACCEPT_LABEL : "Resolve";
  const button = (
    <button
      type="button"
      aria-disabled={disabled}
      aria-describedby={action?.title ? descriptionId : undefined}
      onClick={() => {
        if (!disabled) action?.run();
      }}
      title={action?.title}
      data-dd-action-name={action?.actionName}
      className={twMerge(
        "flex h-8 items-center justify-center rounded-full border border-green-700 bg-white px-4 text-sm font-semibold whitespace-nowrap text-green-700 transition-colors",
        disabled ? "cursor-not-allowed opacity-25" : "cursor-pointer hover:bg-green-50 active:bg-green-100",
      )}
    >
      {/* The check centers with the label, so the room holds it too: pl-6 is its width and the gap. */}
      <span data-widest={ACCEPT_LABEL} className={`${RESERVES_WIDEST} justify-items-center before:pl-6`}>
        <span className={`${RESERVED_TEXT} flex items-center gap-2`}>
          <CheckIcon className="h-4 w-4" strokeWidth={2} />
          {label}
        </span>
      </span>
    </button>
  );
  return (
    <>
      {button}
      {action?.title && (
        <span id={descriptionId} className="sr-only">
          {action.title}
        </span>
      )}
    </>
  );
};

/** Each level's width, with the text it was taken with. */
type MeasuredWidths = Partial<Record<SqueezeLevel, { width: number; text: string }>>;

// What the bar says, but for the position's digits, which the count's slot holds the room for.
// Text only screen readers get (a reason to wait, a state read aloud) doesn't take room, so it's left out. A slot
// with data-widest counts as that text, whatever it shows.
const barText = (box: HTMLElement) => {
  const walker = document.createTreeWalker(box, NodeFilter.SHOW_TEXT);
  let text = "";
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (!node.parentElement?.closest(".sr-only, [data-widest]")) text += node.textContent ?? "";
  }
  box.querySelectorAll<HTMLElement>("[data-widest]").forEach((slot) => {
    text += slot.dataset.widest ?? "";
  });
  return text.replace(/[\d–]+/g, "#");
};

// The width the bar's content needs, even while its box is capped and clips it: the box (border included), or
// its overflowing content plus the border when that is wider.
const naturalWidth = (box: HTMLElement) => {
  const border = box.offsetWidth - box.clientWidth;
  return Math.ceil(Math.max(box.getBoundingClientRect().width || box.offsetWidth, box.scrollWidth + border));
};

/**
 * The review bar: Checkpoint's mark, previous and next, the view picker with the position in that view, and
 * Resolve, at its natural size. It stays up whatever the view lists, so an empty view always has its way back.
 * It squeezes only as a last resort (see SqueezeLevel).
 */
export const SuggestionNavigator: React.FC<SuggestionNavigatorProps> = ({
  items,
  activeId,
  onActivate,
  picker,
  status,
  maxWidth,
  hasOpenItems,
  isDefaultView,
  isPending = false,
  onShowAllOpen,
}) => {
  // Resolving the last card swaps the Resolve button for a fresh one (the card leaves the list), which drops
  // keyboard focus to the page. Put it back on the new button.
  const resolveSlotRef = useRef<HTMLDivElement>(null);
  const resolveHadFocusRef = useRef(false);
  useLayoutEffect(() => {
    const slot = resolveSlotRef.current;
    if (!slot || !resolveHadFocusRef.current) return;
    if (document.activeElement === document.body) slot.querySelector("button")?.focus();
  });

  const activeIndex = items.findIndex((item) => item.id === activeId);
  // The active item can sit outside the view (picked from another view, or a reply moved its thread out of
  // Waiting on you). Stay, so the walk into the view is one click away.
  const current = activeIndex === -1 ? null : items[activeIndex];

  // Prev and Next wrap around, so the last card leads back to the first.
  const goPrev = () => onActivate(items[current ? (activeIndex - 1 + items.length) % items.length : items.length - 1]);
  const goNext = () => onActivate(items[current ? (activeIndex + 1) % items.length : 0]);
  const cannotStep = isPending || items.length === 0 || (items.length === 1 && !!current);

  const isNothingOpen = !hasOpenItems && items.length === 0 && !isPending;
  const showsShowAllOpen = items.length === 0 && hasOpenItems && !isDefaultView && !isPending;
  const hasPosition = !isPending && items.length > 0;
  const position = () => {
    if (hasPosition) return `${current ? activeIndex + 1 : "–"} of ${items.length}`;
    if (isPending || isNothingOpen) return "";
    return showsShowAllOpen ? "0 ·" : "0";
  };
  const positionText = position();
  // Room for the longest position the list has had (digits are tabular, and wider than the dash), kept when a
  // resolved card takes it from 10 to 9, so the arrows don't move then either.
  const [positionDigits, setPositionDigits] = useState(0);
  const digits = hasPosition ? String(items.length).length : 0;
  if (digits > positionDigits) setPositionDigits(digits);
  const widestNumber = "9".repeat(Math.max(digits, positionDigits));

  // Squeezed only as a last resort, decided from the bar's own width (padding and gaps included) at each
  // level, re-measured as it resizes, as its text changes, and once late fonts land.
  const boxRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<MeasuredWidths>({});
  const [level, setLevel] = useState<SqueezeLevel>(0);
  const widths = useMemo(
    () =>
      Object.fromEntries(Object.entries(measured).map(([at, entry]) => [at, entry.width])) as Partial<
        Record<SqueezeLevel, number>
      >,
    [measured],
  );
  const nextLevel = nextSqueezeLevel(level, widths, maxWidth);
  if (nextLevel !== level) setLevel(nextLevel);
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const measure = () => {
      const width = naturalWidth(box);
      const text = barText(box);
      const previous = measured[level];
      // The same level reading different text means the content changed (a view name, Quality Review's state):
      // widths taken with the old text say nothing about it, so start again from the full layout. Levels are only
      // compared with themselves, since a squeeze can swap words for icons.
      if (previous && previous.text !== text) {
        setMeasured(level === 0 ? { 0: { width, text } } : {});
        if (level !== 0) setLevel(0);
        return;
      }
      if (previous?.width !== width || !previous) setMeasured((current) => ({ ...current, [level]: { width, text } }));
    };
    const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    resizeObserver?.observe(box);
    const textObserver = new MutationObserver(measure);
    textObserver.observe(box, { subtree: true, childList: true, characterData: true });
    document.fonts?.addEventListener("loadingdone", measure);
    return () => {
      resizeObserver?.disconnect();
      textObserver.disconnect();
      document.fonts?.removeEventListener("loadingdone", measure);
    };
  }, [level, measured]);
  // Past the last squeeze the bar still has to fit: the view's name shrinks and truncates, so nothing is cut off.
  const isAtFloor = level === TIGHTEST_SQUEEZE;

  return (
    <div
      ref={boxRef}
      data-testid="suggestion-navigator"
      id="suggestion-navigator"
      data-squeeze={level}
      data-truncate={level >= 1 || undefined}
      // Capped at its room at every level, in px rather than a percentage of its shrink-to-fit wrapper, so a
      // frame measured before the squeeze catches up clips inside the bar instead of running past the cards. The
      // squeeze reads the content's own width (naturalWidth).
      className="group/bar flex w-max items-center gap-3 overflow-hidden rounded-2xl border border-limestone-500 bg-white px-4 py-2 text-sm shadow-xl"
      style={{ maxWidth }}
    >
      {status?.(level >= 2)}
      <div className="flex shrink-0 items-center gap-1">
        <NavButton icon={ArrowLeftIcon} onClick={goPrev} disabled={cannotStep} tooltip="Previous" />
        <NavButton icon={ArrowRightIcon} onClick={goNext} disabled={cannotStep} tooltip="Next" />
      </div>
      <div className="h-6 w-px shrink-0 bg-limestone-200" />

      <div className={twMerge("flex shrink-0 items-center gap-2", isAtFloor && "min-w-0 shrink")}>
        {isNothingOpen && <span className="text-sm whitespace-nowrap text-text-tertiary">Nothing open</span>}
        {picker}
        <span
          role="status"
          aria-live="polite"
          aria-label={isNothingOpen ? "Nothing open" : undefined}
          data-widest={hasPosition ? `${widestNumber} of ${widestNumber}` : undefined}
          className={twMerge(
            "text-sm font-medium whitespace-nowrap text-text-primary tabular-nums empty:hidden",
            hasPosition && RESERVES_WIDEST,
          )}
        >
          {positionText && <span className={RESERVED_TEXT}>{positionText}</span>}
        </span>
        {showsShowAllOpen && (
          <Button variant="link" size="xs" className="px-0 text-sm! whitespace-nowrap" onClick={onShowAllOpen}>
            Show all open
          </Button>
        )}
      </div>
      <div className="h-6 w-px shrink-0 bg-limestone-200" />

      <div
        ref={resolveSlotRef}
        className="flex shrink-0"
        onFocus={() => {
          resolveHadFocusRef.current = true;
        }}
        onBlur={(event) => {
          // A blur while the button is still on the page is the user moving on, not the button going away.
          if (event.target.isConnected) resolveHadFocusRef.current = false;
        }}
      >
        {/* The card's checkmark decision, so a suggested edit is accepted here too. */}
        {current ? (
          <CheckAction comment={current}>{(action) => <ResolveButton action={action} />}</CheckAction>
        ) : (
          <ResolveButton action={null} />
        )}
      </div>
    </div>
  );
};
